import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import LiveBar from "./components/LiveBar";
import LangToggle from "./components/LangToggle";
import UncheckedLanguage from "./components/UncheckedLanguage";
import ThemeToggle from "./components/ThemeToggle";
import Manual from "./pages/Manual";
import Quality from "./pages/Quality";
import {
  Link,
  NavLink,
  Navigate,
  Route,
  Routes,
  useNavigate,
} from "react-router-dom";
import { api, setCsrf, type Principal } from "./api";
import BenchDetail from "./pages/BenchDetail";
import Benches from "./pages/Benches";
import ChangePassword from "./pages/ChangePassword";
import Login from "./pages/Login";
import Now from "./pages/Now";
import People from "./pages/People";
import Reports from "./pages/Reports";
import RunDetail from "./pages/RunDetail";
import CampaignPage from "./pages/Campaign";
import Campaigns from "./pages/Campaigns";
import { ProjectDetail, Projects } from "./pages/Projects";
import Runs from "./pages/Runs";
import Scenarios from "./pages/Scenarios";
import { BuildPage, Builds } from "./pages/Builds";
import ProductOverview from "./pages/ProductOverview";
import CasePage from "./pages/CasePage";
import { PrincipalContext } from "./session";
import { ProductProvider, useProduct } from "./product";
import { PAGES, ProductSwitcher, ProductTabs, Search, productTabs, useProducts, type PageKey, type TabKey } from "./components/ProductBar";

/**
 * Shell, session and routing.
 *
 * The session is restored by asking the server rather than by trusting
 * localStorage: the cookie is httpOnly and survives a reload, but the CSRF
 * value lives only in memory, so after F5 the app has a valid session it cannot
 * write with until `/me` hands the value back. Storing it client-side instead
 * would defeat the point of the cookie being httpOnly.
 */
export default function App() {
  const { t } = useTranslation();
  const [principal, setPrincipal] = useState<Principal | null>(null);
  const [passwordRequired, setPasswordRequired] = useState(true);
  // Whether the sign-in form asks for a name as well. Read from the server
  // rather than guessed: an instance that has moved to named accounts would
  // otherwise refuse every correct shared password with no explanation.
  const [namedAccounts, setNamedAccounts] = useState(false);
  const [booted, setBooted] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const me = await api.me();
      setCsrf(me.csrf);
      setPasswordRequired(me.password_required);
      setNamedAccounts(!!me.named_accounts);
      setPrincipal(me.principal.authenticated ? me.principal : null);
    } catch {
      setPrincipal(null);
    } finally {
      setBooted(true);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  if (!booted) {
    return (
      <div className="flex h-full items-center justify-center text-slate-500">
        {t("common.loading")}
      </div>
    );
  }

  if (!principal) {
    return (
      <Login
        onLoggedIn={refresh}
        passwordRequired={passwordRequired}
        namedAccounts={namedAccounts}
      />
    );
  }

  // Instead of the app, not alongside it. The server refuses every write from
  // a must-change session, so rendering the app would give a UI whose every
  // button 403s - which reads as broken rather than as "do this first".
  if (principal.must_change_password) {
    return <ChangePassword onChanged={refresh} />;
  }

  return (
    // Who is signed in, for the pages that decide what to OFFER. What is
    // ALLOWED is decided by the server, per project and per bench.
    <PrincipalContext.Provider value={principal}>
    <ProductProvider>
    <div className="min-h-full bg-slate-50 text-slate-900 dark:bg-slate-950 dark:text-slate-100">
      <Nav principal={principal} onLogout={refresh} />
      <UncheckedLanguage />
      <LiveBar />
      <main className="mx-auto max-w-6xl px-4 py-6">
        <Routes>
          <Route path="/" element={<Navigate to="/now" replace />} />
          <Route path="/now" element={<Now />} />
          <Route path="/projects" element={<Projects />} />
          <Route path="/projects/:id" element={<ProductOverview />} />
          <Route path="/projects/:id/settings" element={<ProjectDetail />} />
          <Route path="/projects/:id/scenarios" element={<Scenarios />} />
          <Route path="/projects/:id/cases/:caseId" element={<CasePage />} />
          <Route path="/projects/:id/builds" element={<Builds />} />
          <Route path="/projects/:id/builds/:key" element={<BuildPage />} />
          <Route path="/benches" element={<Benches />} />
          <Route path="/people" element={<People />} />
          <Route path="/benches/:id" element={<BenchDetail />} />
          <Route path="/quality" element={<Quality />} />
          <Route path="/manual" element={<Manual />} />
          <Route path="/runs" element={<Runs />} />
          <Route path="/runs/:id" element={<RunDetail />} />
          <Route path="/campaigns" element={<Campaigns />} />
          <Route path="/campaigns/:id" element={<CampaignPage />} />
          <Route path="/reports" element={<Reports />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </main>
    </div>
    </ProductProvider>
    </PrincipalContext.Provider>
  );
}

type NavKey = TabKey | PageKey;

/**
 * One row: the product, its tabs, the rest behind More, search, and you.
 *
 * REBUILT 2026-09-28 around the product. The old row was five places to go
 * (Now, Projects, Benches, Runs, Reports) plus a More, and every one of them
 * was a list of ALL products - so the reader chose a product again on every
 * page, and a run was reachable from five places with five different views.
 * Now the product is chosen once, in the switcher, and the tabs are that
 * product's: Overview, Campaigns, Tests, Benches. What belongs to no product
 * (all products, all runs, reports, quality, manual, people) is behind More.
 */
function Nav({
  principal,
  onLogout,
}: {
  principal: Principal;
  onLogout: () => void;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useProduct();
  const { projects, benches, runs, states } = useProducts();

  const signOut = async () => {
    await api.logout();
    setCsrf("");
    onLogout();
    navigate("/");
  };
  const who = principal.user_id !== null ? principal.display_name || principal.name : t("nav.account");
  const account = (
    <div className="space-y-2 px-3 py-2 text-sm">
      {principal.user_id !== null && (
        <p className="text-slate-600 dark:text-slate-300" title={principal.is_admin ? t("nav.adminTitle") : ""}>
          {principal.display_name || principal.name}
          {principal.is_admin ? ` · ${t("nav.admin")}` : ""}
        </p>
      )}
      <div className="flex items-center gap-2 rounded-md bg-brand-700 p-1.5">
        <LangToggle />
        <ThemeToggle />
      </div>
      <button type="button" className="py-1 text-left text-slate-700 underline dark:text-slate-200"
              onClick={signOut}>
        {t("nav.signOut")}
      </button>
    </div>
  );
  const tabs: [string, NavKey][] = id ? productTabs(id).map((tab) => [tab.to, tab.key]) : [];

  return (
    <header className="border-b border-brand-800 bg-brand-700 text-white dark:bg-brand-900">
      <div className="mx-auto flex max-w-6xl flex-wrap items-center gap-2 px-4 py-2.5">
        <Link to="/now" className="mr-1 text-base font-semibold tracking-tight">
          HIL
        </Link>
        <ProductSwitcher states={states} />
        {/* Inline from `sm` up; below it the tabs live in the Menu, so the
            header stays one row on a phone. */}
        <nav className="hidden flex-wrap items-center gap-1 sm:flex" aria-label={t("nav.main")}>
          <ProductTabs />
          <More items={PAGES} label={t("nav.more")} />
        </nav>
        {(!principal.can_write || principal.name === "anonymous") && (
          <span className="rounded bg-amber-400 px-2 py-0.5 text-xs font-semibold text-brand-900"
                title={principal.name === "anonymous" ? "" : t("nav.readOnlyTitle")}>
            {principal.name === "anonymous" ? t("nav.noPassword") : t("nav.readOnly")}
          </span>
        )}
        <div className="ml-auto flex items-center gap-1">
          <Search projects={projects} benches={benches} runs={runs} />
          <nav className="sm:hidden" aria-label={t("nav.main")}>
            <More items={[...tabs, ...PAGES]} label={t("nav.menu")} align="right" footer={account} />
          </nav>
          <div className="hidden sm:block">
            <More items={[]} label={`${who} ▾`} align="right" footer={account} />
          </div>
        </div>
      </div>
    </header>
  );
}

function NotFound() {
  const { t } = useTranslation();
  return <p className="text-slate-500">{t("nav.notFound")}</p>;
}

/**
 * The destinations that are not front-row: Quality, Manual, People.
 *
 * A menu rather than a Settings PAGE with a rail. A rail with one entry is a
 * rail with one entry, and People already carries its own Accounts/Groups
 * tabs; inventing a shell around it would be structure for its own sake, which
 * is the thing this pass keeps removing.
 *
 * Escape and a click anywhere else close it - both are what people try - and
 * the button says whether it is open, because a chevron that only points is a
 * chevron a screen reader cannot read.
 */
function More({ items, label, align = "left", footer }: {
  items: readonly (readonly [string, NavKey])[];
  label: string;
  align?: "left" | "right";
  footer?: React.ReactNode;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const box = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const away = (e: MouseEvent) => {
      if (!box.current?.contains(e.target as Node)) setOpen(false);
    };
    const key = (e: KeyboardEvent) => e.key === "Escape" && setOpen(false);
    document.addEventListener("mousedown", away);
    document.addEventListener("keydown", key);
    return () => {
      document.removeEventListener("mousedown", away);
      document.removeEventListener("keydown", key);
    };
  }, [open]);

  const item =
    "block rounded px-3 py-2 text-sm text-slate-700 hover:bg-slate-100 dark:text-slate-200 dark:hover:bg-slate-800";

  return (
    <div className="relative" ref={box}>
      <button
        aria-expanded={open}
        aria-haspopup="menu"
        onClick={() => setOpen(!open)}
        className="rounded-md px-3 py-1.5 text-sm font-medium text-sky-100 transition-colors hover:bg-brand-600 dark:hover:bg-brand-800"
      >
        {label}
      </button>
      {open && (
        <div
          role="menu"
          className={`absolute ${align === "right" ? "right-0 w-60" : "left-0 w-44"} z-20 mt-1 rounded-lg border border-slate-200 bg-white p-1 shadow-lg dark:border-slate-700 dark:bg-slate-900`}
        >
          {items.map(([to, name]) => (
            <NavLink key={to} role="menuitem" to={to} className={item}
                     onClick={() => setOpen(false)}>
              {t(name)}
            </NavLink>
          ))}
          {footer && <div className="mt-1 border-t border-slate-200 pt-1 dark:border-slate-700">{footer}</div>}
        </div>
      )}
    </div>
  );
}
